
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Navigation } from '@/components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Mail, Phone, Building, User, DollarSign, Calendar, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { CoachEarningsTable } from '@/components/payment/CoachEarningsTable';
import { ClinicBookingDialog } from '@/components/clinic/ClinicBookingDialog';

interface CoachProfile {
  id: string;
  first_name: string;
  last_name: string;
  profile_email: string;
  profile_picture: string;
  club_associations: Array<{
    club_id: string;
    club_name: string;
    title?: string;
    phone?: string;
    email?: string;
    bio?: string;
    profile_picture_url?: string;
  }>;
}

const CoachProfile = () => {
  const { coachId } = useParams<{ coachId: string }>();
  const [coach, setCoach] = useState<CoachProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [showClinicDialog, setShowClinicDialog] = useState(false);

  useEffect(() => {
    if (coachId) {
      fetchCoachProfile(coachId);
    }
  }, [coachId]);

  const fetchCoachProfile = async (id: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('coach_profiles')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      
      // Type cast the data to ensure compatibility
      const coachData: CoachProfile = {
        id: data.id,
        first_name: data.first_name,
        last_name: data.last_name,
        profile_email: data.profile_email,
        profile_picture: data.profile_picture,
        club_associations: data.club_associations as CoachProfile['club_associations'] || []
      };
      
      setCoach(coachData);
    } catch (error) {
      console.error('Error fetching coach profile:', error);
      toast.error('Failed to load coach profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!coach) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Coach Not Found</h1>
            <p className="text-gray-600">The coach profile you're looking for doesn't exist.</p>
          </div>
        </div>
      </div>
    );
  }

  const primaryAssociation = coach.club_associations?.[0];
  const displayPicture = primaryAssociation?.profile_picture_url || coach.profile_picture;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header Card */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                <Avatar className="h-24 w-24">
                  <AvatarImage src={displayPicture} />
                  <AvatarFallback className="text-2xl">
                    {coach.first_name[0]}{coach.last_name[0]}
                  </AvatarFallback>
                </Avatar>
                
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    {coach.first_name} {coach.last_name}
                  </h1>
                  
                  {primaryAssociation?.title && (
                    <p className="text-xl text-gray-600 mb-4">{primaryAssociation.title}</p>
                  )}
                  
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    {primaryAssociation?.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        <a href={`mailto:${primaryAssociation.email}`} className="hover:text-primary">
                          {primaryAssociation.email}
                        </a>
                      </div>
                    )}
                    
                    {primaryAssociation?.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        <a href={`tel:${primaryAssociation.phone}`} className="hover:text-primary">
                          {primaryAssociation.phone}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabbed Content */}
          <Tabs defaultValue="profile" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="profile" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Profile
              </TabsTrigger>
              <TabsTrigger value="earnings" className="flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Earnings
              </TabsTrigger>
              <TabsTrigger value="clinics" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Clinics
              </TabsTrigger>
              <TabsTrigger value="associations" className="flex items-center gap-2">
                <Building className="h-4 w-4" />
                Clubs
              </TabsTrigger>
            </TabsList>

            <TabsContent value="profile" className="space-y-6">
              {/* Bio Card */}
              {primaryAssociation?.bio && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5" />
                      About
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 whitespace-pre-wrap">{primaryAssociation.bio}</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="earnings" className="space-y-6">
              <CoachEarningsTable coachId={coachId} />
            </TabsContent>

            <TabsContent value="clinics" className="space-y-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      Clinic Management
                    </CardTitle>
                    <CardDescription>
                      Create and manage your coaching clinics
                    </CardDescription>
                  </div>
                  <Button onClick={() => setShowClinicDialog(true)} className="flex items-center gap-2">
                    <Plus className="h-4 w-4" />
                    Create Clinic
                  </Button>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-500 text-center py-8">
                    Clinic booking management interface will be displayed here
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="associations" className="space-y-6">
              {/* Club Associations */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building className="h-5 w-5" />
                    Club Associations
                  </CardTitle>
                  <CardDescription>
                    Clubs where this coach is currently active
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {coach.club_associations?.map((association, index) => (
                      <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <h3 className="font-semibold text-gray-900">{association.club_name}</h3>
                          {association.title && (
                            <Badge variant="secondary" className="mt-1">
                              {association.title}
                            </Badge>
                          )}
                        </div>
                        
                        <div className="text-right text-sm text-gray-600">
                          {association.email && (
                            <div className="flex items-center gap-1 justify-end">
                              <Mail className="h-3 w-3" />
                              <span>{association.email}</span>
                            </div>
                          )}
                          {association.phone && (
                            <div className="flex items-center gap-1 justify-end mt-1">
                              <Phone className="h-3 w-3" />
                              <span>{association.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )) || (
                      <p className="text-gray-500 text-center py-4">
                        No club associations found
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Clinic Booking Dialog */}
      {coach.club_associations?.[0] && (
        <ClinicBookingDialog
          isOpen={showClinicDialog}
          onOpenChange={setShowClinicDialog}
          coachId={coachId!}
          clubId={coach.club_associations[0].club_id}
          playerId="" // This would need to be passed or determined
          onSuccess={() => {
            setShowClinicDialog(false);
            toast.success('Clinic created successfully!');
          }}
        />
      )}
    </div>
  );
};

export default CoachProfile;
